<ng-container [formGroup]="formGroup">
  <div class="section">
    <pdq-heading element="h5">Token for Shopify</pdq-heading>
    <div class="controls-wrapper">
      <pdq-link
        label="View detailed installation instructions"
        [external]="true"
        [url]="'https://support.prettydamnquick.com/en/articles/8560867-adding-multiple-pdq-components'"></pdq-link>
      <pdq-input
        [disabled]="true"
        [formControl]="formGroup.get('usageToken')"
        label="Copy & paste this into Shopify's Components by 'Upsell token'"
        [clipboard]="true"></pdq-input>
      <pdq-extension-preview-token [usageTokenControl]="formGroup.get('usageToken')"></pdq-extension-preview-token>
    </div>
  </div>

  <div class="section">
    <pdq-heading element="h5">General</pdq-heading>
    <div class="controls-wrapper">
      <pdq-input [formControl]="formGroup.get('title')" label="Internal name"></pdq-input>
      <pdq-input [formControl]="formGroup.get('internalName')" label="Internal description"></pdq-input>
      <pdq-radio-group formControlName="placementMode" label="Component placement">
        <pdq-radio-button
          [value]="PlacementMode.STANDALONE"
          text="Standalone"
          [checked]="formGroup.value['placementMode'] === PlacementMode.STANDALONE"></pdq-radio-button>
        <pdq-radio-button
          [value]="PlacementMode.LINKED"
          [checked]="formGroup.value['placementMode'] === PlacementMode.LINKED">
          <ng-template #customContent>
            <div class="linked-mode-radio-content">
              <p class="linked-mode-radio-content__title caption-medium-caps">Inside shipping method</p>
              <p class="linked-mode-radio-content__description body-12-regular">
                Go to your
                <pdq-link
                  [clickable]="true"
                  [external]="true"
                  size="small"
                  label="Online Store Editor"
                  [url]="shopService.ecommerceEditorUrl()"></pdq-link>
                to define which shipping method this extension shows up for
              </p>
            </div>
          </ng-template>
        </pdq-radio-button>
      </pdq-radio-group>

      <pdq-checkbox
        [formControl]="formGroup.get('settings.showCompareAtPrice')"
        label="Show 'Compare at price'"
        labelSize="small"
        description="Displays the 'Compare At Price' if it's higher than the product price"></pdq-checkbox>
    </div>
  </div>
  @if (showDiscountSection) {
    <div class="section">
      <pdq-card variant="popover" color="white" class="discounts-card">
        <pdq-heading element="h5">Discounts</pdq-heading>
        <div class="controls-wrapper">
          @if (showAutonomousReadOnlyNotice) {
            <pdq-banner variant="attention" [inline]="true" [closable]="false">
              <p class="body-12-regular">Discount is chosen automatically for this upsell. Contact PDQ to change it.</p>
            </pdq-banner>
          }

          @let showStaticDiscount = showLegacyDiscountControls && showStaticDiscountControls;
          @if (showStaticDiscount) {
            <pdq-checkbox
              [formControl]="formGroup.get('applyDiscountToRecommendedProducts')"
              label="Apply a discount to the recommended products"
              secondaryLabel="Enabling this option will override Shopify's product discounts"
              labelSize="small"></pdq-checkbox>
          }

          @let showAutonomousToggle =
            autonomousDiscountFlagOn &&
            (!showLegacyDiscountControls || formGroup.get('applyDiscountToRecommendedProducts').value);
          @if (showAutonomousToggle) {
            <div class="autonomous-discount__toggle">
              <div class="autonomous-discount__toggle-row">
                <pdq-checkbox
                  label="Let Autonomous Checkout choose the discount"
                  [secondaryLabel]="autonomousDiscountPitch"
                  labelSize="small"
                  [checked]="isAutonomousDiscount"
                  [disabled]="hasGroupDiscounts"
                  (onChange)="autonomousDiscountToggled.emit($event)"></pdq-checkbox>
                <pdq-ai-mark variant="inline"></pdq-ai-mark>
              </div>
              @if (hasGroupDiscounts) {
                <p class="autonomous-discount__hint body-12-regular">Remove per-group discounts first</p>
              }
            </div>
          }

          @if (isAutonomousDiscount && showAutonomousToggle) {
            <div class="flex-row">
              <pdq-dropdown
                [formControl]="formGroup.get('discountConfiguration.discountType')"
                title="Coupon Type"
                [disabled]="true"
                [options]="discountTypeOptions"></pdq-dropdown>
              <pdq-input-arrows
                [formControl]="formGroup.get('discountConfiguration.discountValue')"
                label="Fallback discount"
                widthSize="auto"
                precision="2"
                [min]="0"
                [max]="100"
                symbol="%"></pdq-input-arrows>
            </div>
            <p class="autonomous-discount__helper body-12-regular">Shown when we can't decide in time.</p>

            <div class="autonomous-discount__values">
              <label class="autonomous-discount__values-label caption-medium-caps">Allowed values</label>
              @for (valueControl of allowedDiscountValues?.controls; track valueControl; let i = $index) {
                <div class="autonomous-discount__value-row">
                  <pdq-input-arrows
                    [formControl]="valueControl"
                    widthSize="auto"
                    precision="2"
                    [min]="0"
                    [max]="100"
                    symbol="%"></pdq-input-arrows>
                  <pdq-button
                    icon="trash"
                    size="small"
                    variant="ghost"
                    iconColor="var(--error-default)"
                    matTooltip="Remove value"
                    matTooltipClass="pdq-mat-tooltip"
                    (onClick)="removeAllowedDiscountValue.emit(i)"></pdq-button>
                  @if (valueControl.errors?.allowedDiscountValue) {
                    <span class="autonomous-discount__error body-12-regular">{{
                      valueControl.errors.allowedDiscountValue
                    }}</span>
                  }
                </div>
              }
              <pdq-button
                plain="true"
                icon="Plus"
                size="small"
                label="Add value"
                variant="ghost-light"
                [disabled]="isAddAllowedValueDisabled"
                (onClick)="addAllowedDiscountValue.emit()"></pdq-button>
              @if (allowedDiscountValuesError) {
                <p class="autonomous-discount__error body-12-regular">{{ allowedDiscountValuesError }}</p>
              }
              <p class="autonomous-discount__helper body-12-regular">Changing these values restarts learning.</p>
            </div>

            <div class="flex-row">
              <pdq-input
                [formControl]="formGroup.get('discountConfiguration.labelText')"
                label="Label when a discount applies"
                placeholder="Save {{ '{' }}value{{ '}' }}% today"></pdq-input>
              <pdq-input
                [formControl]="formGroup.get('discountConfiguration.zeroDiscountLabelText')"
                label="Label when no discount applies"
                placeholder="Popular pick"></pdq-input>
            </div>
            <p class="autonomous-discount__helper body-12-regular">
              Use {{ '{' }}value{{ '}' }} for the chosen discount. Leave empty to show nothing.
            </p>
            <pdq-dropdown
              [formControl]="formGroup.get('discountConfiguration.labelColor')"
              title="Label color"
              [options]="labelColorOptions"></pdq-dropdown>
          } @else if (showStaticDiscount && formGroup.get('applyDiscountToRecommendedProducts').value) {
            <div class="flex-row">
              <pdq-dropdown
                [formControl]="formGroup.get('discountConfiguration.discountType')"
                title="Coupon Type"
                [options]="discountTypeOptions"></pdq-dropdown>
              <pdq-input-arrows
                [formControl]="formGroup.get('discountConfiguration.discountValue')"
                label="Coupon value"
                widthSize="auto"
                precision="2"
                [min]="0"
                [max]="
                  formGroup.get('discountConfiguration.discountType').value === DiscountType.Percentage
                    ? 100
                    : undefined
                "
                [symbol]="
                  formGroup.get('discountConfiguration.discountType').value === DiscountType.Percentage
                    ? '%'
                    : defaultCurrencySymbol
                "></pdq-input-arrows>
            </div>
            <div class="flex-row">
              <pdq-input
                [formControl]="formGroup.get('discountConfiguration.labelText')"
                label="Append a label to the product name"
                placeholder="ie.: (25% Off)"
                [invalid]="formGroup.get('discountConfiguration.labelText').invalid"
                [invalidMsg]="
                  formGroup.get('discountConfiguration.labelText').errors?.required ? 'required' : ''
                "></pdq-input>
              <pdq-dropdown
                [formControl]="formGroup.get('discountConfiguration.labelColor')"
                title="Label color"
                [options]="labelColorOptions"></pdq-dropdown>
            </div>
          }
        </div>
      </pdq-card>
    </div>
  }
  <div class="section section--no-divider">
    <pdq-heading element="h5">Minimum Threshold Type</pdq-heading>
    <div class="controls-wrapper">
      <div class="input-group">
        <div class="radio" formGroupName="settings">
          <div class="radio__header">
            <span class="radio__description">When to show this upsell</span>
          </div>
          <pdq-radio-group formControlName="thresholdType">
            <pdq-radio-button
              [value]="IUpsellThresholdTypes.CartValue"
              [checked]="formGroup.get('settings.thresholdType')?.value === IUpsellThresholdTypes.CartValue"
              text="Cart Value">
            </pdq-radio-button>
            <pdq-radio-button
              [value]="IUpsellThresholdTypes.ItemCount"
              [checked]="formGroup.get('settings.thresholdType')?.value === IUpsellThresholdTypes.ItemCount"
              text="Item Count">
              <ng-template #afterContent>
                @if (formGroup.get('settings.thresholdType')?.value === IUpsellThresholdTypes.ItemCount) {
                  <div class="item-count-toggle">
                    <pdq-slide-toggle
                      [formControl]="formGroup.get('settings.onlyCountUpsellItems')"
                      label="Only count items added via this upsell"
                      [preserveLabelCase]="true"
                      [regularLabelWeight]="true">
                    </pdq-slide-toggle>
                  </div>
                }
              </ng-template>
            </pdq-radio-button>
            <pdq-radio-button
              [value]="IUpsellThresholdTypes.NoThreshold"
              [checked]="formGroup.get('settings.thresholdType')?.value === IUpsellThresholdTypes.NoThreshold"
              text="No Threshold">
            </pdq-radio-button>
          </pdq-radio-group>

          @if (formGroup.get('settings.thresholdType')?.value === IUpsellThresholdTypes.NoThreshold) {
            <div class="upsell-title" formGroupName="settings">
              <div class="rich-textarea-container-wrapper">
                <div class="rich-textarea-container">
                  <label class="rich-textarea-label">Display Title</label>
                  <rich-textarea
                    [initialContent]="formGroup.get('settings.noThresholdDisplayTitle')?.value"
                    placeholder="Enter display title..."
                    [enableLinkInsert]="true"
                    [disabledFormats]="['underline']"
                    (contentChange)="formGroup.get('settings.noThresholdDisplayTitle')?.setValue($event)">
                  </rich-textarea>
                </div>
              </div>
            </div>
            <pdq-banner
              variant="attention"
              [bannerTitle]="'Goals Bar Disabled'"
              [content]="'The Goals tab is now disabled. The upsell widget will display products to all customers without any spending threshold.'"
              [closable]="false">
            </pdq-banner>
          }
        </div>
      </div>
    </div>
  </div>
</ng-container>
